
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Building, Users, Settings, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export const OrganizationInfo: React.FC = () => {
  const navigate = useNavigate();

  const { data: organizationData } = useQuery({
    queryKey: ['user-organization'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data: profile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('id', user.id)
        .single();

      if (!profile?.organization_id) return null;

      const { data: org } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', profile.organization_id)
        .single();

      const { data: memberCount } = await supabase
        .from('organization_members')
        .select('id', { count: 'exact' })
        .eq('organization_id', profile.organization_id);

      const { data: userRole } = await supabase
        .from('organization_members')
        .select('role')
        .eq('organization_id', profile.organization_id)
        .eq('user_id', user.id)
        .single();

      return {
        organization: org,
        memberCount: memberCount?.length || 0,
        userRole: userRole?.role || 'member'
      };
    }
  });

  if (!organizationData?.organization) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building className="h-5 w-5" />
            Organization
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground mb-4">You're not part of an organization yet.</p>
          <Button onClick={() => navigate('/settings')}>
            <Plus className="h-4 w-4 mr-2" />
            Join or Create Organization
          </Button>
        </CardContent>
      </Card>
    );
  }

  const { organization, memberCount, userRole } = organizationData;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building className="h-5 w-5" />
          Organization
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <h3 className="font-semibold text-lg">{organization.name}</h3>
          <div className="flex items-center gap-2 mt-2">
            <Badge variant="outline" className="capitalize">
              {userRole}
            </Badge>
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <Users className="h-3 w-3" />
              {memberCount} member{memberCount !== 1 ? 's' : ''}
            </div>
          </div>
        </div>
        
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => navigate('/settings')}
          >
            <Settings className="h-4 w-4 mr-2" />
            Manage Organization
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
